import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  CopyCheck,
  CornerUpRight,
  Download,
  FileMinus2,
  FileQuestion,
  FileX,
  Layers,
  Loader2,
  RotateCw,
  ScanLine,
  Trash2,
  Undo2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { useAuthStore } from '@features/authentication/store/authStore';
import { apiClient } from '@shared/api';
import { ScanImage } from '../ScanImage';
import { FAMILIES } from '../../config/families';
import { documentCaptureService } from '../../services/documentCaptureService';
import { useReviewDecision } from '../../hooks/useReviewDecision';
import { apiErrorInfo } from '../../lib/versionFormat';
import { useDocCaptureEvents } from '../../hooks/useDocCaptureEvents';
import {
  DocReviewReason,
  DocSupersedeState,
  provisionalVersion,
  type DocumentView,
  type ReviewQueueItem,
} from '../../types/documentCapture.types';

/**
 * Replacements-tab action form (86d3rrfdk · W9 arrivals 86d3vbwup). The decision UI is the shipped
 * side-by-side Approve/Keep; what W9 adds is the ARRIVAL REASON — a held update (mismatch / multiple
 * numbers / no barcode), a fewer-pages watcher re-drop, or a terminal unreadable upload, each with
 * its own banner. DecodeFailed is terminal: there is nothing to approve — the card offers Discard
 * (+ Download what arrived). HeldMismatch offers the escape hatch: file the upload on the document
 * its barcode actually reads — shown ONLY when the reviewer can view the TARGET family (the server
 * enforces the same guard; treasury never leaks an actionable affordance).
 */
interface ReplacementReviewPanelProps {
  item: ReviewQueueItem;
}

const BANNER: Partial<
  Record<DocReviewReason, { tone: 'info' | 'warning' | 'destructive'; icon: React.ReactNode; title: string }>
> = {
  [DocReviewReason.SupersedePending]: {
    tone: 'info',
    icon: <CopyCheck className="h-4 w-4" aria-hidden="true" />,
    title: 'A new scan arrived for a document that already has a file',
  },
  [DocReviewReason.FewerPages]: {
    tone: 'warning',
    icon: <FileMinus2 className="h-4 w-4" aria-hidden="true" />,
    title: 'The new file has fewer pages than the one in use',
  },
  [DocReviewReason.HeldMismatch]: {
    tone: 'warning',
    icon: <ScanLine className="h-4 w-4" aria-hidden="true" />,
    title: 'Held: its barcode is for another document',
  },
  [DocReviewReason.HeldMultiple]: {
    tone: 'warning',
    icon: <Layers className="h-4 w-4" aria-hidden="true" />,
    title: 'Held: it reads more than one document number',
  },
  [DocReviewReason.HeldNoBarcode]: {
    tone: 'warning',
    icon: <ScanLine className="h-4 w-4" aria-hidden="true" />,
    title: "Held: no Capitol barcode found — couldn't verify the document",
  },
  [DocReviewReason.DecodeFailed]: {
    tone: 'destructive',
    icon: <FileX className="h-4 w-4" aria-hidden="true" />,
    // 86d403p4v D3 — DecodeFailed now covers TWO different failures and this headline must not pick
    // one. The reason enum could not be split (DiscardReplacementAsync accepts DecodeFailed and
    // nothing else, and an unknown number lands in the wrong tab), so the enum stays coarse and the
    // truth lives in the item's own sentence, rendered verbatim below the title. The old wording —
    // "the file couldn't be read" — was flatly wrong for a replacement whose barcode read perfectly
    // and whose SECOND reading is what failed; see FAILED_TITLE for the pages-exist case.
    title: "Failed: this upload didn't get through",
  },
};

/**
 * The document load, FENCED to the document it was started for (86d3w2jrj FE-2).
 *
 * `forDoc` is what makes it impossible to paint item A's versions while item B is selected: the
 * render below trusts a load ONLY when its `forDoc` equals the current item's docNumber, so a slow,
 * failed or superseded fetch degrades to a skeleton — never to another document's data. Resetting
 * state inside the effect is not enough on its own: effects run AFTER render, so switching items
 * leaves one committed frame showing the new item's banner above the old item's version numbers,
 * page previews and "Approve — make it v{n}" label. Comparing identity at render time closes that
 * frame entirely, and keeps closing it through any future refactor of the effect.
 */
type DocLoad =
  | { status: 'loading'; forDoc: string }
  | { status: 'ready'; forDoc: string; doc: DocumentView }
  | { status: 'error'; forDoc: string; message: string; retryable: boolean };

const HELD_REASONS = new Set([
  DocReviewReason.HeldMismatch,
  DocReviewReason.HeldMultiple,
  DocReviewReason.HeldNoBarcode,
]);

export function ReplacementReviewPanel({ item }: ReplacementReviewPanelProps) {
  const { submit, submitting } = useReviewDecision();

  // Escape hatch (W9, r3 owner decision) — offered ONLY when the reviewer can VIEW the target
  // family; the server refuses regardless, this just keeps the affordance honest.
  //
  // Derived up here, above every early return, because it feeds a HOOK. And it subscribes to the
  // VALUE, not the getter: `useAuthStore((s) => s.hasPermission)` followed by calling it in render
  // hides the permission state from the React Compiler, which then memoizes on the stable function
  // reference and serves a stale affordance (CLAUDE.md 86d3d1z40). A selector that returns the
  // boolean is tracked and re-runs when the permission changes.
  const targetPrefix = item.reasonValue?.split('-')[0] ?? null;
  const targetFamily = targetPrefix ? FAMILIES[targetPrefix] : undefined;
  const canSeeTargetFamily = useAuthStore((s) =>
    targetFamily ? s.hasPermission(targetFamily.permission) : false
  );

  const [load, setLoad] = useState<DocLoad | null>(null);
  /** Bumped by the error card's Try again — re-runs the one effect instead of a second fetch path. */
  const [reloadNonce, setReloadNonce] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const loadSeq = useRef(0);

  const docNumber = item.docNumber;

  /**
   * `silent` refreshes in place: the already-rendered versions stay on screen and are replaced when
   * the new payload lands, instead of collapsing the panel to a skeleton under the reviewer. Used by
   * background refreshes; the first load for an item is never silent.
   */
  const loadDoc = useCallback(
    async (silent: boolean) => {
      if (!docNumber) return;
      // Generation guard: the render fence stops a stale response painting the WRONG document, but a
      // stale one landing LAST would still overwrite the fresh load and strand the panel on a
      // skeleton. Only the newest request may write.
      const seq = ++loadSeq.current;
      if (!silent) setLoad({ status: 'loading', forDoc: docNumber });
      try {
        const view = await documentCaptureService.getDocument(docNumber);
        if (seq !== loadSeq.current) return;
        setLoad({ status: 'ready', forDoc: docNumber, doc: view });
      } catch (err: unknown) {
        if (seq !== loadSeq.current) return;
        // A silent refresh that fails leaves what is on screen alone — it was correct a moment ago,
        // and replacing a working panel with an error card because a background poll blipped is
        // worse than being briefly stale.
        if (silent) return;
        const { status, message } = apiErrorInfo(err);
        setLoad({
          status: 'error',
          forDoc: docNumber,
          // 404 deliberately does not distinguish missing from forbidden — one neutral sentence for
          // both, or this panel becomes a way to probe for treasury documents.
          message:
            status === 404
              ? 'This document isn’t available to you right now.'
              : (message ?? 'Couldn’t load the versions for this document.'),
          retryable: status !== 404,
        });
      }
    },
    [docNumber]
  );

  useEffect(() => {
    // No document on this item — nothing to fetch, and nothing stale can leak through: `forDoc` is a
    // string and `docNumber` is null, so the render fence can never match a leftover load.
    if (!docNumber) return;
    void loadDoc(false);
  }, [docNumber, reloadNonce, loadDoc]);

  /**
   * 86d3w2jrj FE-3 — follow this document live while the reviewer decides. The realistic case is not
   * two people racing: it is a decode finishing, or a replacement being approved elsewhere, while
   * this panel sits open showing the version numbers from whenever it was opened. SILENT, so the
   * evidence the reviewer is comparing never collapses to a skeleton under them.
   */
  useDocCaptureEvents({
    docNumber,
    enabled: !!docNumber,
    debounceMs: 250,
    onEvent: () => void loadDoc(true),
  });

  const decide = useCallback(
    (approve: boolean) =>
      submit(() => documentCaptureService.decideReplacement(item.id, approve, item.linkEpoch ?? 0), {
        successMessage: approve ? 'Replacement approved — the new scan is now current' : 'Kept the current version',
      }),
    [submit, item.id, item.linkEpoch]
  );

  const discard = useCallback(
    () =>
      submit(() => documentCaptureService.discardUpload(item.id, item.linkEpoch ?? 0), {
        successMessage: `Upload discarded — ${item.docNumber ?? 'the document'} still serves its current version.`,
      }),
    [submit, item.id, item.linkEpoch, item.docNumber]
  );

  const refile = useCallback(
    () =>
      submit(() => documentCaptureService.refileUpload(item.id, item.linkEpoch ?? 0), {
        successMessage: `Filed on ${item.reasonValue} — ${item.docNumber ?? 'this document'} is untouched.`,
      }),
    [submit, item.id, item.linkEpoch, item.reasonValue, item.docNumber]
  );

  // THE FENCE. Everything below reads `doc` — and `doc` exists only when the settled load belongs to
  // the item currently on screen. Any other combination (still loading, failed, superseded by a
  // newer selection, or an item with no document at all) yields null and takes an explicit branch.
  const fenced = load && docNumber && load.forDoc === docNumber ? load : null;
  const doc = fenced?.status === 'ready' ? fenced.doc : null;

  /** Download the parked upload itself ("Download what arrived") — scoped to THIS item's document/version. */
  const downloadArrived = useCallback(async () => {
    const pendingVersion = doc ? provisionalVersion(doc.versions)?.version : undefined;
    if (!item.docNumber || pendingVersion == null) return;
    setDownloading(true);
    try {
      const res = await apiClient.get<Blob>(
        documentCaptureService.fileUrl(item.docNumber, pendingVersion, true),
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.docNumber}-v${pendingVersion}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Couldn’t download the uploaded file.');
    } finally {
      setDownloading(false);
    }
  }, [doc, item.docNumber]);

  // An item with no document at all. After FE-1 this is unreachable from this panel — Kind=Scan
  // items route to Scans — but it is cheap defence, and it is honest instead of a blank card.
  if (!docNumber) {
    return (
      <EmptyState
        icon={FileQuestion}
        title="This item isn’t attached to a document"
        description="There are no versions to compare. Resolve it from the Scans tab, where it can be assigned or rejected."
      />
    );
  }

  if (!fenced || fenced.status === 'loading') {
    // Skeleton, not a spinner (UI gate): the shape of what is coming, in the place it will appear.
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full rounded-lg" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-52 w-full rounded-lg" />
          <Skeleton className="h-52 w-full rounded-lg" />
        </div>
        <Skeleton className="h-9 w-64 rounded-md" />
      </div>
    );
  }

  if (fenced.status === 'error') {
    // The old code swallowed this and rendered the decision buttons anyway — over whatever document
    // happened to be in state. A reviewer must never be offered Approve without the evidence.
    return (
      <div
        role="alert"
        className="space-y-2 rounded-lg border border-destructive/35 bg-destructive/5 p-3"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
          <div className="min-w-0 space-y-1.5">
            <p className="text-sm text-foreground">{fenced.message}</p>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Nothing has changed — this document still serves whichever version it served before.
            </p>
            {fenced.retryable && (
              <Button
                variant="outline"
                size="sm"
                className="mt-1.5 h-7"
                onClick={() => setReloadNonce((n) => n + 1)}
              >
                <RotateCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Try again
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const current = doc?.versions.find((v) => v.supersedeState === DocSupersedeState.Active);
  const pending = doc ? provisionalVersion(doc.versions) : undefined;

  const banner = BANNER[item.reason] ?? BANNER[DocReviewReason.SupersedePending]!;
  const isHeld = HELD_REASONS.has(item.reason);
  const isFailed = item.reason === DocReviewReason.DecodeFailed;

  /**
   * 86d403p4v D4 — DOES THIS UPLOAD HAVE PAGES TO LOOK AT? Gate the compare pane on that, never on
   * `isFailed`.
   *
   * The two failures that share the DecodeFailed reason differ exactly here. A file the decoder could
   * not open never reached the image-rendering step, so there is genuinely nothing to show. A file
   * whose SECOND reading failed decoded fine, and its page images were written to the store before the
   * verdict was ever taken — so `unreadable` was hiding evidence the reviewer needs, under a caption
   * ("the file never produced a page") that was simply untrue.
   *
   * `pageCount` is a trustworthy signal for this and it was not always: until M6 the verify handler's
   * park hard-coded 0, so a row with real pages reported none. M6 made it carry the payload's count,
   * which is what lets this condition be written at all.
   */
  const arrivedPageCount = pending?.pageCount ?? 0;
  const hasArrivedPages = arrivedPageCount > 0;

  const canRefile =
    item.reason === DocReviewReason.HeldMismatch && !!item.reasonValue && canSeeTargetFamily;

  return (
    <div className="space-y-4">
      {/* arrival-reason banner — title from the reason, body = the item's verbatim sentence */}
      <div
        className={cn(
          'flex items-start gap-2 rounded-lg border px-3 py-2.5',
          // /8, not /7. Tailwind's opacity scale runs in fives plus the three values registered in
          // tailwind.config.js (8, 12, 14); /7 is on neither list, so Tailwind emitted no rule at all and
          // all three of these banners painted with NO FILL. Same defect the config's own comment
          // describes from the ~18 `/12` sites, found again by a repo-wide sweep on 2026-08-18.
          banner.tone === 'info' && 'border-info/30 bg-info/8 text-info',
          banner.tone === 'warning' && 'border-warning/30 bg-warning/8 text-warning',
          banner.tone === 'destructive' && 'border-destructive/30 bg-destructive/8 text-destructive'
        )}
      >
        <span className="mt-0.5 shrink-0">{banner.icon}</span>
        <div className="min-w-0">
          <p className="text-xs font-semibold">
            {isFailed && hasArrivedPages
              ? "Failed: the independent check couldn't be completed"
              : banner.title}
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{item.reasonDetail}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <VersionColumn label="Current" version={current?.version} docNumber={item.docNumber} v={current?.version} />
        <VersionColumn
          label={isFailed ? 'What arrived' : 'New scan'}
          version={pending?.version}
          docNumber={item.docNumber}
          v={pending?.version}
          highlight
          // D4 — image presence, not failure. A second-reader failure leaves the pages perfectly
          // viewable, and hiding them is what made the reviewer unable to check the very thing the
          // panel is asking them to decide about.
          unreadable={isFailed && !hasArrivedPages}
        />
      </div>

      {isFailed ? (
        // Terminal — no Approve: nothing was ever promoted; v-current is untouched.
        <div className="space-y-2 rounded-lg border border-destructive/25 bg-destructive/5 p-3">
          <div className="flex items-center gap-1.5">
            <FileX className="h-4 w-4 text-destructive" aria-hidden="true" />
            <p className="text-xs font-semibold text-destructive">There is nothing to approve</p>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {hasArrivedPages
              ? // 86d403p4v D3 — the file is fine here and the reviewer can see it, so telling them to
                // re-export the PDF sends them to fix something that is not broken. It is the check
                // that failed, and the only verb this item accepts is Discard: the reason enum stays
                // DecodeFailed so the discard verb resolves it, which means there is no Approve to
                // offer even though the pages look right.
                'Nothing was promoted and the current version is untouched. The file itself arrived intact — it is on the right — but the independent check never completed, so nothing confirmed it. Discard this upload and send the file again; if it fails the same way twice, the check is the problem, not the file.'
              : 'Nothing was promoted and the current version is untouched. Download what arrived if you want to check it, then discard the upload — whoever uploaded it will need to re-export the PDF.'}
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="destructive" disabled={submitting} onClick={() => void discard()}>
              {submitting ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="mr-1.5 h-4 w-4" aria-hidden />
              )}
              Discard the upload
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={downloading || pending == null}
              onClick={() => void downloadArrived()}
            >
              {downloading ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Download className="mr-1.5 h-4 w-4" aria-hidden />
              )}
              Download what arrived
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={submitting} onClick={() => void decide(true)}>
            {submitting ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Check className="mr-1.5 h-4 w-4" aria-hidden />
            )}
            {isHeld && pending?.version != null ? `Approve — make it v${pending.version}` : 'Approve replacement'}
          </Button>
          <Button variant="outline" size="sm" disabled={submitting} onClick={() => void decide(false)}>
            <Undo2 className="mr-1.5 h-4 w-4" aria-hidden />
            Keep current
          </Button>
          {canRefile && (
            <Button variant="outline" size="sm" disabled={submitting} onClick={() => void refile()}>
              <CornerUpRight className="mr-1.5 h-4 w-4" aria-hidden />
              File it on {item.reasonValue} instead
            </Button>
          )}
        </div>
      )}

      {isHeld && (
        <p className="flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
          The current version stays live until you decide — nothing changed for anyone reading this
          document today.
        </p>
      )}
    </div>
  );
}

function VersionColumn({
  label,
  version,
  docNumber,
  v,
  highlight,
  unreadable,
}: {
  label: string;
  version: number | null | undefined;
  docNumber: string | null;
  v: number | null | undefined;
  highlight?: boolean;
  unreadable?: boolean;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {version != null && <span className="text-xs tabular-nums text-muted-foreground">v{version}</span>}
      </div>
      {unreadable ? (
        // A DecodeFailed upload has no page images — there is nothing to render.
        <div className="flex h-48 flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-destructive/40 text-xs text-muted-foreground">
          <FileX className="h-5 w-5 text-destructive" aria-hidden="true" />
          No preview — the file never produced a page
        </div>
      ) : docNumber && v != null ? (
        <ScanImage
          docNumber={docNumber}
          version={v}
          page={1}
          alt={`${label} version of ${docNumber}`}
          className={`h-48 w-full rounded-lg border ${highlight ? 'border-primary' : 'border-border'}`}
        />
      ) : (
        <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-border text-xs text-muted-foreground">
          No preview
        </div>
      )}
    </div>
  );
}
